import React, { useEffect, useState } from 'react';
import { supabaseClient } from '../lib/supabase';
import { Button } from '../components/ui/Button';
import { Pagination } from '../components/ui/Pagination';
import { SearchBar } from '../components/ui/SearchBar';
import { AlertCircle, CheckCircle, XCircle, Clock, Filter } from 'lucide-react';
import { useActivityLog } from '../contexts/ActivityLogContext';
interface Claim {
  id: string;
  orderId: string;
  orderNo: string;
  customerName: string;
  company: string;
  branch: string;
  type: string;
  status: string;
  priority: string;
  description: string;
  resolution: string | null;
  createdAt: string;
  resolvedAt: string | null;
  createdBy: string;
}
export function ClaimsPage() {
  const [claims, setClaims] = useState<Claim[]>([]);
  const [filteredClaims, setFilteredClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const {
    addLog
  } = useActivityLog();
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredClaims.length / itemsPerPage);
  const paginatedClaims = filteredClaims.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  useEffect(() => {
    loadClaims();
    // Sincronización en tiempo real
    const subscription = supabaseClient.subscribeToClaims(() => {
      console.log('Claim updated, reloading...');
      loadClaims();
    });
    return () => {
      subscription?.unsubscribe();
    };
  }, []);
  useEffect(() => {
    filterClaims();
  }, [claims, searchQuery, statusFilter, priorityFilter]);
  const loadClaims = async () => {
    try {
      setLoading(true);
      const data = await supabaseClient.getClaims();
      setClaims(data);
    } catch (error) {
      console.error('Error loading claims:', error);
    } finally {
      setLoading(false);
    }
  };
  const filterClaims = () => {
    let filtered = claims;
    // Filtrar por estado
    if (statusFilter !== 'all') {
      filtered = filtered.filter(claim => claim.status === statusFilter);
    }
    // Filtrar por prioridad
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(claim => claim.priority === priorityFilter);
    }
    // Filtrar por búsqueda
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(claim => claim.orderNo.toLowerCase().includes(query) || claim.customerName.toLowerCase().includes(query) || claim.company.toLowerCase().includes(query) || claim.description.toLowerCase().includes(query));
    }
    setFilteredClaims(filtered);
    setCurrentPage(1); // Reset a primera página cuando cambian filtros
  };
  const handleAcceptClaim = async (claim: Claim) => {
    if (!confirm(`¿Aceptar la reclamación #${claim.orderNo}?`)) return;
    try {
      await supabaseClient.updateClaim(claim.id, {
        status: 'Aceptada',
        resolvedAt: new Date().toISOString(),
        resolution: 'Reclamación aceptada por el administrador'
      });
      addLog('Reclamación aceptada', `Orden #${claim.orderNo} - ${claim.customerName}`, 'claim');
      loadClaims();
    } catch (error) {
      console.error('Error accepting claim:', error);
      alert('Error al aceptar la reclamación');
    }
  };
  const handleRejectClaim = async (claim: Claim) => {
    const reason = prompt(`¿Rechazar la reclamación #${claim.orderNo}?\n\nIngresa el motivo del rechazo:`);
    if (!reason) return;
    try {
      await supabaseClient.updateClaim(claim.id, {
        status: 'Rechazada',
        resolvedAt: new Date().toISOString(),
        resolution: reason
      });
      addLog('Reclamación rechazada', `Orden #${claim.orderNo} - ${claim.customerName}`, 'claim');
      loadClaims();
    } catch (error) {
      console.error('Error rejecting claim:', error);
      alert('Error al rechazar la reclamación');
    }
  };
  const getStatusBadge = (status: string) => {
    const styles = {
      Pendiente: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      Aceptada: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      Rechazada: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    };
    return styles[status as keyof typeof styles] || styles.Pendiente;
  };
  const getPriorityBadge = (priority: string) => {
    const styles = {
      Alta: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      Media: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      Baja: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
    };
    return styles[priority as keyof typeof styles] || styles.Media;
  };
  if (loading) {
    return <div className="flex items-center justify-center h-96" data-id="element-729">
        <div className="text-center" data-id="element-730">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4" data-id="element-731"></div>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-732">
            Cargando reclamaciones...
          </p>
        </div>
      </div>;
  }
  return <div className="p-6 max-w-7xl mx-auto" data-id="element-733">
      <div className="mb-6" data-id="element-734">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-735">
          Reclamaciones de Clientes
        </h1>
        <p className="text-gray-600 dark:text-gray-400" data-id="element-736">
          Gestiona las reclamaciones y quejas de los clientes
        </p>
      </div>

      {/* Filtros y búsqueda */}
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-4 mb-6" data-id="element-737">
        <div className="flex flex-col lg:flex-row gap-4" data-id="element-738">
          <div className="flex-1" data-id="element-739">
            <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Buscar por orden, cliente, empresa..." data-id="element-740" />
          </div>

          <div className="flex gap-2" data-id="element-741">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-742">
              <option value="all" data-id="element-743">Todos los estados</option>
              <option value="Pendiente" data-id="element-744">Pendiente</option>
              <option value="Aceptada" data-id="element-745">Aceptada</option>
              <option value="Rechazada" data-id="element-746">Rechazada</option>
            </select>

            <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-747">
              <option value="all" data-id="element-748">Todas las prioridades</option>
              <option value="Alta" data-id="element-749">Alta</option>
              <option value="Media" data-id="element-750">Media</option>
              <option value="Baja" data-id="element-751">Baja</option>
            </select>
          </div>
        </div>

        {/* Estadísticas rápidas */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-800" data-id="element-752">
          <div className="flex items-center gap-3" data-id="element-753">
            <Clock className="text-yellow-500" size={20} data-id="element-754" />
            <div data-id="element-755">
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-756">
                Pendientes
              </p>
              <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-757">
                {claims.filter(c => c.status === 'Pendiente').length}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-758">
            <CheckCircle className="text-green-500" size={20} data-id="element-759" />
            <div data-id="element-760">
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-761">
                Aceptadas
              </p>
              <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-762">
                {claims.filter(c => c.status === 'Aceptada').length}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-763">
            <XCircle className="text-red-500" size={20} data-id="element-764" />
            <div data-id="element-765">
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-766">
                Rechazadas
              </p>
              <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-767">
                {claims.filter(c => c.status === 'Rechazada').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de reclamaciones */}
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden" data-id="element-768">
        {paginatedClaims.length === 0 ? <div className="p-12 text-center" data-id="element-769">
            <AlertCircle size={48} className="mx-auto mb-4 text-gray-400" data-id="element-770" />
            <p className="text-gray-500 dark:text-gray-400" data-id="element-771">
              {searchQuery || statusFilter !== 'all' || priorityFilter !== 'all' ? 'No se encontraron reclamaciones con los filtros aplicados' : 'No hay reclamaciones registradas'}
            </p>
          </div> : <div className="divide-y divide-gray-200 dark:divide-gray-800" data-id="element-772">
            {paginatedClaims.map(claim => <div key={claim.id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-773">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4" data-id="element-774">
                  <div className="flex-1" data-id="element-775">
                    <div className="flex items-start gap-3 mb-3" data-id="element-776">
                      <AlertCircle className={claim.priority === 'Alta' ? 'text-red-500' : claim.priority === 'Media' ? 'text-orange-500' : 'text-blue-500'} size={24} data-id="element-777" />
                      <div className="flex-1" data-id="element-778">
                        <div className="flex items-center gap-2 mb-1" data-id="element-779">
                          <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-780">
                            Orden #{claim.orderNo}
                          </h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(claim.status)}`} data-id="element-781">
                            {claim.status}
                          </span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityBadge(claim.priority)}`} data-id="element-782">
                            {claim.priority}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-783">
                          {claim.customerName} • {claim.company} -{' '}
                          {claim.branch}
                        </p>
                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-2" data-id="element-784">
                          <span className="font-medium" data-id="element-785">Tipo:</span>{' '}
                          {claim.type}
                        </p>
                        <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-786">
                          {claim.description}
                        </p>
                        {claim.resolution && <div className="mt-3 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg" data-id="element-787">
                            <p className="text-sm font-medium text-gray-900 dark:text-white mb-1" data-id="element-788">
                              Resolución:
                            </p>
                            <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-789">
                              {claim.resolution}
                            </p>
                          </div>}
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2" data-id="element-790">
                          Creada:{' '}
                          {new Date(claim.createdAt).toLocaleString('es-DO')}
                          {claim.resolvedAt && <>
                              {' '}
                              • Resuelta:{' '}
                              {new Date(claim.resolvedAt).toLocaleString('es-DO')}
                            </>}
                        </p>
                      </div>
                    </div>
                  </div>

                  {claim.status === 'Pendiente' && <div className="flex gap-2" data-id="element-791">
                      <Button onClick={() => handleAcceptClaim(claim)} variant="primary" size="sm" leftIcon={<CheckCircle size={16} data-id="element-793" />} data-id="element-792">
                        Aceptar
                      </Button>
                      <Button onClick={() => handleRejectClaim(claim)} variant="outline" size="sm" leftIcon={<XCircle size={16} data-id="element-795" />} data-id="element-794">
                        Rechazar
                      </Button>
                    </div>}
                </div>
              </div>)}
          </div>}

        {/* Paginación */}
        {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredClaims.length} itemsPerPage={itemsPerPage} data-id="element-796" />}
      </div>
    </div>;
}